import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import '../../styles/statsCard.css';
import { useSales } from '../../hooks/useSales';

const RevenueStats = ({ variant = "daily" }) => {
  const { sales = [] } = useSales();
  const now = useMemo(() => new Date(), []);

  const totalRevenue = useMemo(() =>
    sales.reduce((sum, sale) => sum + (sale.total || 0), 0), [sales]);

  const monthlyRevenue = useMemo(() => {
    const currentMonth = now.toISOString().slice(0, 7);
    return sales
      .filter(sale => sale.createdAt.startsWith(currentMonth))
      .reduce((sum, sale) => sum + (sale.total || 0), 0);
  }, [sales, now]);

  const dailyRevenue = useMemo(() => {
    const todayStr = now.toISOString().split("T")[0];
    return sales
      .filter(sale => sale.createdAt.split("T")[0] === todayStr)
      .reduce((sum, sale) => sum + (sale.total || 0), 0);
  }, [sales, now]);

  const yesterdayRevenue = useMemo(() => {
    const yesterdayStr = new Date(now.getTime() - 86400000).toISOString().split("T")[0];
    return sales
      .filter(sale => sale.createdAt.split("T")[0] === yesterdayStr)
      .reduce((sum, sale) => sum + (sale.total || 0), 0);
  }, [sales, now]);

  const lastMonthRevenue = useMemo(() => {
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return sales
      .filter(sale => {
        const created = new Date(sale.createdAt);
        return created.getMonth() === lastMonth.getMonth() && created.getFullYear() === lastMonth.getFullYear();
      })
      .reduce((sum, sale) => sum + (sale.total || 0), 0);
  }, [sales, now]);

  let revenueValue = 0;
  let previousRevenue = 0;
  let title = "";

  switch (variant) {
    case "daily":
      revenueValue = dailyRevenue;
      previousRevenue = yesterdayRevenue;
      title = "Today's Revenue";
      break;
    case "monthly":
      revenueValue = monthlyRevenue;
      previousRevenue = lastMonthRevenue;
      title = "Monthly Revenue";
      break;
    case "total":
    default:
      revenueValue = totalRevenue;
      previousRevenue = 0;
      title = "Total Revenue";
  }

  // Cap percentage at 100% max (both positive and negative)
  const rawPercentage = previousRevenue
    ? ((revenueValue - previousRevenue) / previousRevenue) * 100
    : revenueValue > 0 ? 100 : 0;

  const percentage = Math.min(Math.abs(rawPercentage), 100) * (rawPercentage < 0 ? -1 : 1);
  const isPositive = percentage >= 0;

  return (
    <div className="stat-card revenue">
      <h3>{title}</h3>
      <div className="value">₹{revenueValue.toLocaleString("en-IN")}</div>
      {variant !== "total" && (
        <div className={`change ${isPositive ? "positive" : "negative"}`}>
          <FontAwesomeIcon icon={isPositive ? "arrow-up" : "arrow-down"} className="icon" />
          <span>{Math.abs(percentage).toFixed(1)}% {isPositive ? "increase" : "decrease"}</span>
        </div>
      )}
    </div>
  );
};

export default RevenueStats;
